import React from "react";
import {
  Unlink, Users, Database, Gauge, FileX2, MonitorOff,
  Search, Map, Scale, PenLine, Rocket, TrendingUp,
  Compass, Layers, Settings, Cog, Wrench, UserRound,
  Check, Quote, ShoppingCart, Workflow, LayoutDashboard, Puzzle,
  Sparkles, Zap, BarChart3, RefreshCw, Building2, Factory, HardHat,
  Landmark, Briefcase, Truck, TrendingUp as Growth, Award, Target,
  MonitorSmartphone, SearchCheck,
} from "lucide-react";

const Logo = ({ dark }) => (
  <img
    src="/brand/logo-gt-mark.svg"
    alt="GT Strategy"
    className={`h-[3.2cqw] w-auto ${dark ? "" : "pres-logo-dark opacity-80"}`}
  />
);

const Shell = ({ n, dark, children }) => (
  <div className={`pres-slide-inner ${dark ? "pres-dark" : "pres-light"}`}>
    <div className="relative z-10 h-full w-full px-[4.2cqw] pt-[3cqw] pb-[5.6cqw]">{children}</div>
    <div className="pres-footer z-10">
      <Logo dark={dark} />
      <span className={`text-[0.95cqw] font-semibold tracking-[0.25em] ${dark ? "text-[#FCF9F0]/60" : "text-[#B8AA98]"}`}>
        {String(n).padStart(2, "0")}
      </span>
    </div>
  </div>
);

const Title = ({ children, className = "" }) => (
  <h2 className={`pres-title text-[2.65cqw] ${className}`}>{children}</h2>
);

const Sub = ({ children, className = "" }) => (
  <p className={`text-[1.3cqw] leading-[1.55] text-[#4a463f] mt-[1cqw] ${className}`}>{children}</p>
);

const Closing = ({ children, light }) => (
  <div className={`${light ? "pres-closing-light" : "pres-closing"} px-[2cqw] py-[1.3cqw] text-[1.2cqw] leading-[1.5] font-medium text-center`}>
    {children}
  </div>
);

const CoverBg = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(85cqw 55cqw at 82% -8%, rgba(111,156,143,0.30), transparent 62%), radial-gradient(70cqw 50cqw at 4% 112%, rgba(14,96,87,0.60), transparent 58%), radial-gradient(55cqw 42cqw at 104% 104%, rgba(0,26,24,0.65), transparent 62%)",
      }}
    />
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 90" preserveAspectRatio="none" fill="none">
      <path d="M-5 68 C 28 52, 62 84, 96 62 C 126 42, 150 58, 165 44" stroke="#6F9C8F" strokeOpacity="0.30" strokeWidth="0.45" />
      <path d="M-5 76 C 33 60, 66 92, 101 70 C 130 52, 152 66, 165 54" stroke="#6F9C8F" strokeOpacity="0.18" strokeWidth="0.4" />
      <path d="M-5 84 C 38 70, 70 98, 106 78 C 134 62, 154 74, 165 64" stroke="#6F9C8F" strokeOpacity="0.10" strokeWidth="0.35" />
      <path d="M-5 18 C 25 30, 60 6, 95 16 C 125 24, 145 8, 165 16" stroke="#FCF9F0" strokeOpacity="0.10" strokeWidth="0.4" />
      <path d="M-5 10 C 30 20, 64 -2, 100 8 C 128 15, 148 2, 165 8" stroke="#FCF9F0" strokeOpacity="0.06" strokeWidth="0.35" />
    </svg>
  </div>
);

const CoverLockup = ({ d }) => (
  <>
    <img src="/brand/logo-gt-mark.svg" alt="GT Strategy" className="h-[9cqw] w-auto mb-[1.4cqw]" />
    <p className="text-[1.1cqw] font-semibold uppercase tracking-[0.32em] text-[#9FC3B6]">{d.slogan}</p>
    <div className="w-[6cqw] h-[2px] bg-[#6F9C8F]/70 my-[2.6cqw]" />
  </>
);

/* 01 — Capa */
export const Slide1 = ({ d }) => (
  <div className="pres-slide-inner pres-dark">
    <CoverBg />
    <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-[10cqw]">
      <CoverLockup d={d} />
      <h1 className="text-[2.75cqw] font-semibold leading-[1.32] max-w-[62cqw]">{d.title}</h1>
      <p className="text-[1.3cqw] leading-[1.6] text-[#FCF9F0]/70 max-w-[54cqw] mt-[1.8cqw]">{d.sub}</p>
    </div>
  </div>
);

/* 02 — Contexto / dor */
const painIcons = [Unlink, Users, Database, Gauge, FileX2, MonitorOff];
export const Slide2 = ({ d }) => (
  <Shell n={2}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <p className="text-[1.6cqw] font-medium text-[#6F9C8F] mt-[0.3cqw]">{d.title2}</p>
      <Sub>{d.sub}</Sub>
      <div className="grid grid-cols-3 gap-[1.4cqw] flex-1 content-center">
        {d.items.map((it, i) => {
          const Icon = painIcons[i];
          return (
            <div key={i} className="pres-card px-[1.6cqw] py-[1.9cqw] flex items-center gap-[1.2cqw]">
              <Icon className="w-[2.2cqw] h-[2.2cqw] shrink-0 text-[#184A45]" strokeWidth={1.5} />
              <span className="text-[1.25cqw] leading-[1.4] font-medium">{it}</span>
            </div>
          );
        })}
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 03 — Manifesto */
export const Slide3 = ({ d }) => (
  <Shell n={3}>
    <div className="h-full flex flex-col justify-center max-w-[76cqw]">
      <Quote className="w-[3.4cqw] h-[3.4cqw] text-[#184A45] mb-[2cqw]" fill="#184A45" strokeWidth={0} />
      <p className="text-[2.5cqw] leading-[1.5] font-semibold text-[#184A45]">{d.text}</p>
      <div className="w-[7cqw] h-[3px] bg-[#184A45] mt-[2.4cqw]" />
    </div>
  </Shell>
);

/* 04 — Modelo mental */
const dimIcons = [Compass, Layers, Cog, Workflow, UserRound, Wrench];
const dimPos = [
  { left: "50%", top: "7%" },
  { left: "88%", top: "29%" },
  { left: "88%", top: "71%" },
  { left: "50%", top: "93%" },
  { left: "12%", top: "71%" },
  { left: "12%", top: "29%" },
];
export const Slide4 = ({ d }) => (
  <Shell n={4}>
    <div className="grid grid-cols-[1.15fr_1fr] gap-[2.6cqw] h-full">
      <div className="flex flex-col">
        <Title>{d.title}</Title>
        <Sub className="mt-[0.8cqw]">{d.sub}</Sub>
        <ul className="mt-[1.6cqw] space-y-[0.85cqw]">
          {d.questions.map((q, i) => (
            <li key={i} className="flex items-start gap-[0.8cqw] text-[1.15cqw] leading-[1.42]">
              <Check className="w-[1.3cqw] h-[1.3cqw] mt-[0.1cqw] shrink-0 text-[#6F9C8F]" strokeWidth={2.4} />
              <span>{q}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-[1.4cqw]">
          <Closing light>{d.closing}</Closing>
        </div>
      </div>
      <div className="relative self-center w-full aspect-square max-h-full scale-[0.92]">
        <div className="absolute inset-[9%] rounded-full border border-dashed border-[#B8AA98]" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[37%] aspect-square rounded-full bg-[#184A45] flex items-center justify-center text-center text-[#FCF9F0] text-[1.3cqw] font-bold leading-[1.3] px-[1.4cqw]">
          {d.center}
        </div>
        {d.dims.map((dim, i) => {
          const Icon = dimIcons[i];
          return (
            <div
              key={i}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-[0.35cqw]"
              style={dimPos[i]}
            >
              <div className="w-[4.4cqw] h-[4.4cqw] rounded-full bg-[#F4EFE5] border border-[#B8AA98] flex items-center justify-center">
                <Icon className="w-[2cqw] h-[2cqw] text-[#184A45]" strokeWidth={1.5} />
              </div>
              <span className="text-[1cqw] font-semibold uppercase tracking-[0.08em] text-[#184A45] bg-[#F4EFE5]/90 px-[0.4cqw]">
                {dim}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </Shell>
);

/* 05 — Matriz (animada: 0=linhas, 1=cabeçalho, 2=células) */
const rowIcons = [Search, Settings, MonitorSmartphone, RefreshCw];
export const Slide5 = ({ d, step = 2 }) => (
  <Shell n={5}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub>{d.sub}</Sub>
      <div className="mt-[1.6cqw] border border-[#D8D1C6] rounded-[0.6cqw] overflow-hidden flex-1 flex flex-col">
        <div className={`pres-frag ${step >= 1 ? "in" : ""} grid grid-cols-[1.1fr_1.3fr_1.3fr_1.3fr] bg-[#184A45] text-[#FCF9F0]`}>
          <div />
          {d.cols.map((c, i) => (
            <div key={i} className="px-[1.4cqw] py-[1cqw] text-[1.15cqw] font-bold uppercase tracking-[0.12em] text-center">
              {c}
            </div>
          ))}
        </div>
        {d.rows.map((r, ri) => {
          const Icon = rowIcons[ri];
          return (
            <div key={ri} className={`grid grid-cols-[1.1fr_1.3fr_1.3fr_1.3fr] flex-1 ${ri % 2 ? "bg-[#EFE9DC]" : "bg-[#FAF6EC]"} border-t border-[#D8D1C6]`}>
              <div className="px-[1.4cqw] py-[1.2cqw] flex items-center gap-[0.8cqw]">
                <Icon className="w-[1.5cqw] h-[1.5cqw] text-[#184A45]" strokeWidth={1.6} />
                <span className="text-[1.15cqw] font-bold uppercase tracking-[0.06em] text-[#184A45]">{r.t}</span>
              </div>
              {r.c.map((cell, ci) => (
                <div
                  key={ci}
                  className={`pres-frag ${step >= 2 ? "in" : ""} px-[1.4cqw] py-[1.2cqw] text-[1.08cqw] leading-[1.4] border-l border-[#D8D1C6] flex items-center`}
                  style={{ transitionDelay: step >= 2 ? `${(ri * 3 + ci) * 70}ms` : "0ms" }}
                >
                  {cell}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <p className="mt-[1.4cqw] text-center text-[1.15cqw] font-semibold text-[#184A45]">{d.closing}</p>
    </div>
  </Shell>
);

/* 06 — STRATFLOW (animado: cada clique revela bolinha + seta) */
const flowIcons = [Search, Map, Scale, PenLine, Rocket, TrendingUp];
export const Slide6 = ({ d, step = 5 }) => (
  <Shell n={6}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[66cqw]">{d.sub}</Sub>
      <div className="flex items-start justify-between flex-1 px-[0.5cqw] pt-[4cqw]">
        {d.steps.map((s, i) => {
          const Icon = flowIcons[i];
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <span className={`pres-frag ${step >= i ? "in" : ""} text-[1.7cqw] text-[#6F9C8F] mt-[1.8cqw]`}>→</span>
              )}
              <div className={`pres-frag ${step >= i ? "in" : ""} flex flex-col items-center text-center w-[12cqw]`}>
                <div className="w-[5.6cqw] h-[5.6cqw] rounded-full bg-[#FAF6EC] border border-[#B8AA98] flex items-center justify-center mb-[1cqw]">
                  <Icon className="w-[2.4cqw] h-[2.4cqw] text-[#184A45]" strokeWidth={1.5} />
                </div>
                <span className="text-[0.85cqw] font-bold tracking-[0.2em] text-[#B8AA98]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[1.15cqw] font-semibold leading-[1.3] text-[#184A45] mt-[0.3cqw]">{s.t}</span>
                {s.note && (
                  <span className="text-[0.92cqw] italic text-[#8a7f6d] leading-[1.3] mt-[0.35cqw]">{s.note}</span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 07 — HandOff Mapping */
const netNodes = [
  { x: 50, y: 18 }, { x: 84, y: 36 }, { x: 76, y: 76 }, { x: 50, y: 55 },
  { x: 24, y: 76 }, { x: 16, y: 36 },
];
const netEdges = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0], [0, 3], [1, 3], [5, 3]];
export const Slide7 = ({ d }) => (
  <Shell n={7}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[72cqw]">{d.sub}</Sub>
      <div className="grid grid-cols-[1fr_0.9fr_1.1fr] gap-[2.2cqw] flex-1 items-center mt-[0.6cqw]">
        <svg viewBox="0 0 100 92" className="w-full max-h-[25cqw]">
          {netEdges.map(([a, b], i) => (
            <line key={i} x1={netNodes[a].x} y1={netNodes[a].y} x2={netNodes[b].x} y2={netNodes[b].y} stroke="#6F9C8F" strokeWidth="0.6" strokeDasharray="2 1.5" />
          ))}
          {netNodes.map((n, i) => (
            <circle key={i} cx={n.x} cy={n.y} r={i === 3 ? 7 : 5.2} fill={i === 3 ? "#184A45" : "#FAF6EC"} stroke="#184A45" strokeWidth="0.8" />
          ))}
        </svg>
        <div>
          <p className="text-[1.1cqw] font-bold uppercase tracking-[0.12em] text-[#184A45] mb-[0.9cqw]">{d.map_label}</p>
          <ul className="space-y-[0.5cqw]">
            {d.map.map((m, i) => (
              <li key={i} className="flex items-center gap-[0.7cqw] text-[1.1cqw]">
                <span className="w-[0.5cqw] h-[0.5cqw] rounded-full bg-[#6F9C8F] shrink-0" />
                {m}
              </li>
            ))}
          </ul>
        </div>
        <div className="pres-card px-[1.8cqw] py-[1.5cqw]">
          <p className="text-[1.1cqw] font-bold uppercase tracking-[0.12em] text-[#184A45] mb-[0.9cqw]">{d.reveal_label}</p>
          <ul className="space-y-[0.6cqw]">
            {d.reveals.map((r, i) => (
              <li key={i} className="flex items-start gap-[0.7cqw] text-[1.05cqw] leading-[1.35]">
                <Check className="w-[1.1cqw] h-[1.1cqw] mt-[0.12cqw] shrink-0 text-[#6F9C8F]" strokeWidth={2.4} />
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 08 — Gestão conectada (antes/depois) */
const scatterDots = [
  [18, 22], [42, 14], [70, 26], [86, 50], [60, 46], [28, 54], [12, 74], [46, 78], [76, 82], [88, 16],
];
const connNodes = [[50, 20], [80, 40], [72, 76], [28, 76], [20, 40], [50, 50]];
const connEdges = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [0, 5], [1, 5], [2, 5], [3, 5], [4, 5]];
export const Slide8 = ({ d }) => (
  <Shell n={8}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[68cqw]">{d.sub}</Sub>
      <div className="grid grid-cols-[1fr_1.15fr] gap-[3cqw] flex-1 items-center mt-[0.5cqw]">
        <div className="grid grid-cols-2 gap-[1.6cqw]">
          <div className="pres-card p-[1.4cqw]">
            <p className="text-[1.05cqw] font-bold uppercase tracking-[0.18em] text-[#B8AA98] text-center mb-[0.6cqw]">{d.before}</p>
            <svg viewBox="0 0 100 96" className="w-full">
              {scatterDots.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="3.6" fill="#D8D1C6" stroke="#B8AA98" strokeWidth="0.6" />
              ))}
            </svg>
          </div>
          <div className="pres-card p-[1.4cqw]">
            <p className="text-[1.05cqw] font-bold uppercase tracking-[0.18em] text-[#184A45] text-center mb-[0.6cqw]">{d.after}</p>
            <svg viewBox="0 0 100 96" className="w-full">
              {connEdges.map(([a, b], i) => (
                <line key={i} x1={connNodes[a][0]} y1={connNodes[a][1]} x2={connNodes[b][0]} y2={connNodes[b][1]} stroke="#6F9C8F" strokeWidth="0.7" />
              ))}
              {connNodes.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={i === 5 ? 5.5 : 4} fill={i === 5 ? "#184A45" : "#FAF6EC"} stroke="#184A45" strokeWidth="0.8" />
              ))}
            </svg>
          </div>
        </div>
        <ul className="space-y-[0.9cqw]">
          {d.items.map((it, i) => (
            <li key={i} className="flex items-start gap-[0.8cqw] text-[1.2cqw] leading-[1.4]">
              <Check className="w-[1.3cqw] h-[1.3cqw] mt-[0.12cqw] shrink-0 text-[#6F9C8F]" strokeWidth={2.4} />
              {it}
            </li>
          ))}
        </ul>
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 09 — Tecnologia */
const techIcons = [Users, ShoppingCart, Workflow, LayoutDashboard, Puzzle];
export const Slide9 = ({ d }) => (
  <Shell n={9}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[66cqw]">{d.sub}</Sub>
      <div className="grid grid-cols-5 gap-[1.3cqw] flex-1 items-center">
        {d.items.map((it, i) => {
          const Icon = techIcons[i];
          return (
            <div key={i} className="pres-card px-[1.3cqw] py-[2cqw] h-[18cqw] flex flex-col items-center text-center">
              <div className="w-[4cqw] h-[4cqw] rounded-full bg-[#184A45]/8 border border-[#D8D1C6] flex items-center justify-center mb-[1.2cqw]">
                <Icon className="w-[1.9cqw] h-[1.9cqw] text-[#184A45]" strokeWidth={1.5} />
              </div>
              <span className="text-[1.1cqw] leading-[1.42] font-medium">{it}</span>
            </div>
          );
        })}
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 10 — IA aplicada */
const gainColors = {
  e: { bg: "#184A45", label: "#FCF9F0" },
  q: { bg: "#6F9C8F", label: "#FCF9F0" },
  d: { bg: "#B8AA98", label: "#222222" },
  s: { bg: "#222222", label: "#FCF9F0" },
};
export const Slide10 = ({ d }) => (
  <Shell n={10}>
    <div className="h-full flex flex-col">
      <div className="flex items-start justify-between gap-[2cqw]">
        <div>
          <Title>{d.title}</Title>
          <Sub className="max-w-[58cqw]">{d.sub}</Sub>
        </div>
        <div className="flex flex-col gap-[0.45cqw] shrink-0 pt-[0.4cqw]">
          {Object.entries(d.gains).map(([k, v]) => (
            <div key={k} className="flex items-center gap-[0.55cqw] text-[0.95cqw] font-semibold">
              <span className="w-[0.95cqw] h-[0.95cqw] rounded-full" style={{ background: gainColors[k].bg }} />
              {v}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-[1cqw] flex-1 items-center mt-[0.6cqw]">
        {d.items.map((it, i) => (
          <div key={i} className="pres-card px-[1.1cqw] py-[1.2cqw] h-[14cqw] flex flex-col">
            <span
              className="self-start text-[0.75cqw] font-bold uppercase tracking-[0.1em] px-[0.65cqw] py-[0.25cqw] rounded-full mb-[0.75cqw]"
              style={{ background: gainColors[it.g].bg, color: gainColors[it.g].label }}
            >
              {d.gains[it.g]}
            </span>
            <span className="text-[0.96cqw] leading-[1.4] font-medium">{it.t}</span>
          </div>
        ))}
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 11 — Experiência */
const expIcons = [Wrench, Users, Award, Target, MonitorSmartphone, Building2];
export const Slide11 = ({ d }) => (
  <Shell n={11}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[68cqw]">{d.sub}</Sub>
      <div className="flex items-center justify-between flex-1 px-[0.5cqw]">
        {d.items.map((it, i) => {
          const Icon = expIcons[i];
          return (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center text-center w-[13cqw]">
                <div className="w-[5cqw] h-[5cqw] rounded-full bg-[#FAF6EC] border border-[#B8AA98] flex items-center justify-center mb-[0.9cqw]">
                  <Icon className="w-[2.2cqw] h-[2.2cqw] text-[#184A45]" strokeWidth={1.5} />
                </div>
                <span className="text-[1.08cqw] font-medium leading-[1.38]">{it}</span>
              </div>
              {i < d.items.length - 1 && <span className="text-[1.5cqw] text-[#6F9C8F] -mt-[3.5cqw]">→</span>}
            </React.Fragment>
          );
        })}
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 12 — Pontos de partida */
const startIcons = [SearchCheck, Map, Sparkles, Settings, Zap, BarChart3, Puzzle];
export const Slide12 = ({ d }) => (
  <Shell n={12}>
    <div className="h-full flex flex-col">
      <Title>{d.title}</Title>
      <Sub className="max-w-[64cqw]">{d.sub}</Sub>
      <div className="flex-1 flex flex-col justify-center gap-[1.2cqw] mt-[0.5cqw]">
        <div className="grid grid-cols-4 gap-[1.2cqw]">
          {d.items.slice(0, 4).map((it, i) => {
            const Icon = startIcons[i];
            return (
              <div key={i} className="pres-card px-[1.2cqw] py-[1.5cqw] flex flex-col items-center text-center h-[11.5cqw] justify-center">
                <Icon className="w-[2cqw] h-[2cqw] text-[#184A45] mb-[0.9cqw]" strokeWidth={1.5} />
                <span className="text-[1.08cqw] leading-[1.4] font-medium">{it}</span>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-[1.2cqw] px-[8cqw]">
          {d.items.slice(4).map((it, i) => {
            const Icon = startIcons[i + 4];
            return (
              <div key={i} className="pres-card px-[1.2cqw] py-[1.5cqw] flex flex-col items-center text-center h-[11.5cqw] justify-center">
                <Icon className="w-[2cqw] h-[2cqw] text-[#184A45] mb-[0.9cqw]" strokeWidth={1.5} />
                <span className="text-[1.08cqw] leading-[1.4] font-medium">{it}</span>
              </div>
            );
          })}
        </div>
      </div>
      <Closing light>{d.closing}</Closing>
    </div>
  </Shell>
);

/* 13 — Mercados e contextos */
const marketIcons = [HardHat, Landmark, Factory, Building2, Briefcase, Truck, Growth];
const contextIcons = [TrendingUp, Unlink, MonitorSmartphone, Settings, Target, RefreshCw];
export const Slide13 = ({ d }) => (
  <Shell n={13}>
    <div className="h-full flex flex-col">
      <Title className="max-w-[76cqw]">{d.title}</Title>
      <div className="grid grid-cols-2 gap-[2.5cqw] flex-1 content-center mt-[1.4cqw]">
        {[
          { label: d.markets_label, items: d.markets, icons: marketIcons },
          { label: d.contexts_label, items: d.contexts, icons: contextIcons },
        ].map((col, ci) => (
          <div key={ci} className="pres-card px-[2cqw] py-[1.9cqw]">
            <p className="text-[1.2cqw] font-bold uppercase tracking-[0.18em] text-[#184A45] border-b border-[#D8D1C6] pb-[0.8cqw] mb-[1.1cqw]">
              {col.label}
            </p>
            <ul className="space-y-[0.85cqw]">
              {col.items.map((it, i) => {
                const Icon = col.icons[i];
                return (
                  <li key={i} className="flex items-center gap-[0.9cqw] text-[1.15cqw] leading-[1.35]">
                    <Icon className="w-[1.4cqw] h-[1.4cqw] shrink-0 text-[#6F9C8F]" strokeWidth={1.6} />
                    {it}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </Shell>
);

/* 14 — Encerramento */
export const Slide14 = ({ d }) => (
  <div className="pres-slide-inner pres-dark">
    <CoverBg />
    <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-[10cqw]">
      <CoverLockup d={d} />
      <h2 className="text-[2.5cqw] font-semibold leading-[1.32] max-w-[58cqw]">{d.title}</h2>
      <p className="text-[1.3cqw] leading-[1.6] text-[#FCF9F0]/70 max-w-[54cqw] mt-[1.8cqw]">{d.sub}</p>
    </div>
  </div>
);
